/**
 * Business Hours Service - Multi-tenant operating hours management
 * Manages business operating hours (Morning, Afternoon, Evening shifts)
 * Session-based business_id (no manual business_id parameter needed)
 */
import http from './httpClient';

class BusinessHoursService {
  /**
   * Get all operating hours for the current business
   * @returns {Promise} - Promise resolving to business hours data
   */
  static async getBusinessHours() {
    try {
      const data = await http.get('/business-hours');
      if (!data.success) throw new Error(data.error || 'Failed to fetch business hours');
      return data;
    } catch (error) {
      console.error('Error fetching business hours:', error);
      throw error;
    }
  }

  /**
   * Get shift times for Stock Out form dropdown
   * @param {string} dayOfWeek - Optional day of week filter (Monday, Tuesday, etc.)
   * @returns {Promise} - Promise resolving to formatted shift data
   */
  static async getShiftTimes(dayOfWeek = null) {
    try {
      const params = dayOfWeek ? `?day_of_week=${dayOfWeek}` : '';
      const data = await http.get(`/business-hours/shifts${params}`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch shift times');
      return data;
    } catch (error) {
      console.error('Error fetching shift times:', error);
      throw error;
    }
  }

  /**
   * Get current shift based on actual business hours and current time
   * @returns {Promise} - Promise resolving to current shift data
   */
  static async getCurrentShift() {
    try {
      const data = await http.get('/business-hours/current-shift');
      if (!data.success) throw new Error(data.error || 'Failed to fetch current shift');
      return data;
    } catch (error) {
      console.error('Error fetching current shift:', error);
      throw error;
    }
  }

  /**
   * Bulk upsert business hours (used by Settings page)
   * @param {Array} hours - Array of hour objects with day_of_week, shift_type, is_open, opens_at, closes_at
   * @returns {Promise} - Promise resolving to updated hours data
   */
  static async bulkUpdateHours(hours) {
    try {
      const data = await http.post('/business-hours/bulk', { hours });
      if (!data.success) throw new Error(data.error || 'Failed to update business hours');
      return data;
    } catch (error) {
      console.error('Error updating business hours:', error);
      throw error;
    }
  }

  /**
   * Update a single business hour entry
   * @param {number} hoursId - The hours_id to update
   * @param {Object} updates - Object with is_open, opens_at, closes_at
   * @returns {Promise} - Promise resolving to updated hour data
   */
  static async updateBusinessHour(hoursId, updates) {
    try {
      const data = await http.put(`/business-hours/${hoursId}`, updates);
      if (!data.success) throw new Error(data.error || 'Failed to update business hour');
      return data;
    } catch (error) {
      console.error('Error updating business hour:', error);
      throw error;
    }
  }

  /**
   * Delete a business hour entry
   * @param {number} hoursId - The hours_id to delete
   * @returns {Promise} - Promise resolving to deletion confirmation
   */
  static async deleteBusinessHour(hoursId) {
    try {
      const data = await http.delete(`/business-hours/${hoursId}`);
      if (!data.success) throw new Error(data.error || 'Failed to delete business hour');
      return data;
    } catch (error) {
      console.error('Error deleting business hour:', error);
      throw error;
    }
  }

  /**
   * Helper: Get default hours template for a new business
   * @returns {Array} - Array of default business hours (7 days x 3 shifts = 21 entries)
   */
  static getDefaultHoursTemplate() {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const shifts = [
      { shift_type: 'Morning', opens_at: '06:00:00', closes_at: '12:00:00' },
      { shift_type: 'Afternoon', opens_at: '12:00:00', closes_at: '18:00:00' },
      { shift_type: 'Evening', opens_at: '18:00:00', closes_at: '00:00:00' }
    ];

    const hours = [];
    days.forEach(day => {
      shifts.forEach(shift => {
        hours.push({
          day_of_week: day,
          shift_type: shift.shift_type,
          is_open: true,
          opens_at: shift.opens_at,
          closes_at: shift.closes_at
        });
      });
    });

    return hours;
  }

  /**
   * Helper: Format time from "HH:MM:SS" to "HH:MM AM/PM"
   * @param {string} timeString - Time in "HH:MM:SS" format
   * @returns {string} - Formatted time like "6:00 AM"
   */
  static formatTime(timeString) {
    if (!timeString) return '';
    
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const min = minutes || '00';
    
    if (hour === 0) return `12:${min} AM`;
    if (hour < 12) return `${hour}:${min} AM`;
    if (hour === 12) return `12:${min} PM`;
    return `${hour - 12}:${min} PM`;
  }

  /**
   * Helper: Parse time from "HH:MM AM/PM" to "HH:MM:SS"
   * @param {string} timeString - Time in "HH:MM AM/PM" format
   * @returns {string} - Time in "HH:MM:SS" format
   */
  static parseTime(timeString) {
    if (!timeString) return '00:00:00';
    
    const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return '00:00:00';
    
    let [, hours, minutes, period] = match;
    hours = parseInt(hours);
    
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    
    return `${String(hours).padStart(2, '0')}:${minutes}:00`;
  }
}

export default BusinessHoursService;
